// The strip of app affordances that sits above the document.
//
// WHY it is outside `.doc-sheet` and not inside it: the sheet is the paper the
// customer receives, so nothing that only exists inside this application may
// live in it. The toolbar is app chrome — it scrolls away with the page and is
// removed from print.
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  LogisticsCalculationStatus,
  type LogisticsCalculationDetailResponse,
} from '@magimp/shared';
import { Button } from '../../../ui/index.js';
import { LogisticsCalculationPublishAction } from './LogisticsCalculationPublishAction.js';
import { LogisticsCalculationStatusBadge } from './LogisticsCalculationStatusBadge.js';

export function DocumentToolbar({
  onBack,
  code,
  children,
}: {
  onBack: () => void;
  code?: string;
  children?: ReactNode;
}) {
  const { t } = useTranslation('logisticsCalculations');
  return (
    <div className="doc-toolbar" data-testid="rl-document-toolbar">
      <Button kind="ghost" size="sm" data-testid="rl-document-back" onClick={onBack}>
        {t('actions.back')}
      </Button>
      {code ? (
        <span className="doc-toolbar-code doc-n" data-testid="rl-document-toolbar-code">
          {code}
        </span>
      ) : null}
      <div className="doc-toolbar-actions">{children}</div>
    </div>
  );
}

/** Turns the browser's own print pipeline into the document's PDF export. */
export function DocumentPrintButton() {
  const { t } = useTranslation('logisticsCalculations');
  return (
    <Button kind="ghost" size="sm" data-testid="rl-document-print" onClick={() => window.print()}>
      {t('actions.downloadPdf')}
    </Button>
  );
}

/**
 * The manager's frame around a saved document.
 *
 * INVARIANT: back, the public code, the status badge, the views figure and
 * print are unconditional — they state what the document IS, and a reader may
 * always print what they can already see. Everything that WRITES to the
 * document is gated on `logisticsCalculations.manage` AND the status that
 * accepts it, because the backend refuses the rest
 * (`@RequiresPermission('logisticsCalculations.manage')` on update, publish,
 * revoke and delete): offering a read-only actor three buttons that fail is a surface
 * regression, not an authorization hole. `onEdit` is null on a screen too
 * narrow to fill the document in, which withholds the edit affordance without
 * touching publish or revoke.
 */
export function DocumentManagerToolbar({
  detail,
  canManage,
  htmlViews,
  revoking,
  deleting,
  onBack,
  onEdit,
  onCopyLink,
  onRevoke,
  onDelete,
}: {
  detail: LogisticsCalculationDetailResponse;
  canManage: boolean;
  htmlViews: number | null;
  revoking: boolean;
  deleting: boolean;
  onBack: () => void;
  onEdit: (() => void) | null;
  onCopyLink: () => void;
  onRevoke: () => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation('logisticsCalculations');
  const calculation = detail.calculation;
  const published = calculation.status === LogisticsCalculationStatus.PUBLISHED;
  return (
    <DocumentToolbar onBack={onBack} code={calculation.publicCode}>
      <LogisticsCalculationStatusBadge status={calculation.status} />
      <span className="doc-toolbar-meta" data-testid="rl-html-views">
        {`${t('detail.htmlViews')}: ${htmlViews ?? '—'}`}
      </span>
      <DocumentPrintButton />
      {published && detail.publicLink ? (
        <Button kind="ghost" size="sm" data-testid="rl-copy-link" onClick={onCopyLink}>
          {t('actions.copyLink')}
        </Button>
      ) : null}
      {canManage && calculation.status === LogisticsCalculationStatus.DRAFT ? (
        <>
          {onEdit ? (
            <Button kind="ghost" size="sm" data-testid="rl-document-edit" onClick={onEdit}>
              {t('actions.edit')}
            </Button>
          ) : null}
          <LogisticsCalculationPublishAction calculation={calculation} />
          <Button
            kind="danger"
            size="sm"
            disabled={deleting}
            data-testid="rl-delete"
            onClick={onDelete}
          >
            {t('actions.delete')}
          </Button>
        </>
      ) : null}
      {canManage && published ? (
        <Button
          kind="danger"
          size="sm"
          disabled={revoking}
          data-testid="rl-revoke"
          onClick={onRevoke}
        >
          {t('actions.revoke')}
        </Button>
      ) : null}
    </DocumentToolbar>
  );
}
